import { useContext, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router';
import { isEmpty } from 'lodash';

import Loader from '../components/Loader';
import { AuthContext } from '../globalContext/auth/authProvider';
import { RouteContext } from '../globalContext/routes/routeProvider';
import { SubscriptionContext } from '../globalContext/subscriptions/subscriptionsProvider';
import { UserContext } from '../globalContext/user/userProvider';
import useFetch from '../services/apiService/useFetch';
import useGetAllPurchasedSubjects from '../hooks/useGetAllPurchasedSubjects';
import getToken from '../auth/getToken';
import { setUserSubscriptions } from '../globalContext/subscriptions/subscriptionsActions';
import { setRequestedRoute } from '../globalContext/routes/routeActions';
import { setUserInfo } from '../globalContext/user/userActions';
import { getItem } from '../services/localStorageService';
import API_ENDPOINTS from '../consts/endpoint';
import {
  API_RESPONSE_STATUS_CODE,
  LOCAL_STORAGE_KEYS,
} from '../consts/constants';
import { SubscriptionState, UserState } from '../types';
import { ROUTES } from '../route/routes';
import { redirectToLogin } from '../utils/logoutRedirect';
import { ThemeContext } from '../globalContext/theme/themeProvider';
import { toggleColorMode } from '../globalContext/theme/themeActions';
import {
  applyBrandTheme,
  extractBuIdFromPayload,
  reconcileBrandFromUserBuId,
} from '../theme/brands';

function withPrivateAccess(Component: React.FC) {
  return (props: any) => {
    const [authState] = useContext(AuthContext);
    const [userInfo, dispatchUserInfo] = useContext(UserContext);
    const [subscriptionInfo, dispatchSubscriptonInfo] =
      useContext(SubscriptionContext);
    const [, routeDispatch] = useContext(RouteContext);
    const [, themeDispatch] = useContext(ThemeContext);

    const { isGettingSubjectsList, savePurchasedSubjectsList } =
      useGetAllPurchasedSubjects();

    const userId = getItem(LOCAL_STORAGE_KEYS.USER_ID);
    const userType = getItem(LOCAL_STORAGE_KEYS.USER_TYPE);

    const location = useLocation();

    const { fetchData, isLoading } = useFetch({
      url: API_ENDPOINTS.USER_INFO,
      otherOptions: {
        skipApiCallOnMount: true,
      },
    });

    const { fetchData: fetchSubscriptions } = useFetch({
      url: API_ENDPOINTS.SUBSCRIPTIONS,
      otherOptions: {
        skipApiCallOnMount: true,
      },
    });

    const saveSubscriptions = async () => {
      const subscriptionResponse = await fetchSubscriptions({});
      if (subscriptionResponse?.status === API_RESPONSE_STATUS_CODE.SUCCESS) {
        const subscriptionInfo: SubscriptionState = {
          subscription: subscriptionResponse.data,
        };
        dispatchSubscriptonInfo(setUserSubscriptions(subscriptionInfo));
        if (
          subscriptionResponse.data?.paid ||
          subscriptionResponse.data?.has_ic3_subscription
        ) {
          savePurchasedSubjectsList();
        }
      }
    };

    const logoutUser = () => {
      routeDispatch(
        setRequestedRoute({
          route: `${location.pathname}${location.search}`,
        }),
      );
      redirectToLogin();
    };

    useEffect(() => {
      const token = getToken();
      const isStorageMissing = !userId || !userType || !token;

      if (isStorageMissing) {
        if (authState?.token) {
          logoutUser();
        }
        return;
      }

      // Fetch user info if userInfo is empty OR if profileImg is missing/empty (to get avatar after login)
      const needsUserInfo = isEmpty(userInfo) || !userInfo?.profileImg || userInfo?.profileImg === '';
      if (authState?.token && userId && userType && needsUserInfo) {
        (async () => {
          const response = await fetchData({});
          if (response?.status === API_RESPONSE_STATUS_CODE.SUCCESS) {
            const {
              name,
              email,
              id,
              image,
              gender,
              grade,
              profile_completion,
            } = response.data;
            const userInfoObj: UserState = {
              firstName: name?.split(' ')[0] || userInfo?.firstName || '',
              lastName: name?.split(' ')[1] || userInfo?.lastName || '',
              email: email || userInfo?.email || '',
              id: id || userId,
              userType: userType,
              profileImg: image || userInfo?.profileImg || '',
              grade: grade || userInfo?.grade || '',
              gender: gender !== undefined ? gender : userInfo?.gender,
              profileCompletion: profile_completion || userInfo?.profileCompletion || 0,
            };
            dispatchUserInfo(setUserInfo(userInfoObj));

            const brandKey = reconcileBrandFromUserBuId(
              extractBuIdFromPayload(response.data),
            );
            themeDispatch(
              toggleColorMode({ brand: applyBrandTheme(brandKey) }),
            );
          } else {
            logoutUser();
          }
        })();
      }

      if (authState?.token && isEmpty(subscriptionInfo)) {
        saveSubscriptions();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState]);

    const token = getToken();
    const isAuthenticated = Boolean(userId && userType && token);

    if (!isAuthenticated) {
      return <Navigate to={ROUTES.LOGIN} replace />;
    }

    if (
      !userInfo ||
      !subscriptionInfo ||
      isLoading ||
      isGettingSubjectsList
    ) {
      return <Loader />;
    }
    return <Component {...props} />;
  };
}

export default withPrivateAccess;
